// TelcoTrack — printable D5 fiber SVG.
// Cassette / slot / pigtail: incoming ends on the left of a block, outgoing on the right.
// Napřímo (via: direct, no cassette splice): caption above, two colors meet mid-line.

import { FIBER_COLOR_HEX, fiberNumberToColorsOrNull } from './fiber-color-code.constants';
import type { RozvadecOccupancySchema } from './rozvadec-occupancy-schema.types';

type DiagramMode = 'block' | 'direct';

type DiagramRow = {
  id: string;
  mode: DiagramMode;
  leftFiber: number | null;
  rightFiber: number | null;
  leftColor: string;
  rightColor: string;
  caption: string;
};

const LEFT_X = 28;
const BLOCK_X = 148;
const BLOCK_W = 124;
const BLOCK_H = 22;
const RIGHT_X = 412;
const MID_X = (LEFT_X + RIGHT_X) / 2;
const ROW_H = 48;
const TOP = 32;

function escapeXml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function fiberHex(physicalFiber: number | null): string {
  if (physicalFiber == null) return '#6b7280';
  const assignment = fiberNumberToColorsOrNull(physicalFiber, 'DEFAULT', 'OUTER');
  return assignment ? FIBER_COLOR_HEX[assignment.fiberColor] : '#2563eb';
}

function diagramRows(schema: RozvadecOccupancySchema): DiagramRow[] {
  const rows: DiagramRow[] = [];
  const cassetteLabel = (cassetteId: string): string =>
    schema.graph.cassettes.find((item) => item.id === cassetteId)?.printLabel ?? '';

  for (const splice of schema.graph.splices) {
    const left = splice.fibers[0] ?? null;
    const right = splice.fibers[1] ?? null;
    if (!left && !right) continue;
    const assignment = schema.graph.assignments.find((item) => item.spliceId === splice.id);
    rows.push({
      id: splice.id,
      mode: 'block',
      leftFiber: left?.physicalFiber ?? null,
      rightFiber: right?.physicalFiber ?? assignment?.physicalFiber ?? null,
      leftColor: fiberHex(left?.physicalFiber ?? null),
      rightColor: fiberHex(right?.physicalFiber ?? assignment?.physicalFiber ?? null),
      caption: splice.printLabel,
    });
  }

  const splicedConnectors = new Set(
    schema.graph.splices
      .filter((item) => item.kind === 'pigtail')
      .map((item) => item.id),
  );
  for (const connector of schema.graph.connectors) {
    if (!connector.incoming) continue;
    if (connector.pigtailSpliceId && splicedConnectors.has(connector.pigtailSpliceId)) {
      continue;
    }
    const assignment = schema.graph.assignments.find((item) => item.connectorId === connector.id);
    rows.push({
      id: connector.id,
      mode: 'block',
      leftFiber: connector.incoming.physicalFiber,
      rightFiber: assignment?.physicalFiber ?? null,
      leftColor: fiberHex(connector.incoming.physicalFiber),
      rightColor: fiberHex(assignment?.physicalFiber ?? null),
      caption: [cassetteLabel(connector.cassetteId), `slot ${connector.adapterPosition}`, connector.printLabel]
        .filter(Boolean)
        .join(' · '),
    });
  }

  for (const assignment of schema.graph.assignments) {
    if (assignment.via !== 'direct' || assignment.spliceId || assignment.connectorId) continue;
    rows.push({
      id: assignment.id,
      mode: 'direct',
      leftFiber: assignment.sourcePhysicalFiber,
      rightFiber: assignment.physicalFiber,
      leftColor: fiberHex(assignment.sourcePhysicalFiber),
      rightColor: fiberHex(assignment.physicalFiber),
      caption: '',
    });
  }

  return rows;
}

function blockRowSvg(row: DiagramRow, y: number): string {
  const blockLeft = BLOCK_X;
  const blockRight = BLOCK_X + BLOCK_W;
  const blockTop = y - BLOCK_H / 2;
  const captionX = BLOCK_X + BLOCK_W / 2;
  const captionY = blockTop - 8;
  const incoming =
    row.leftFiber != null
      ? `<path class="fiber-line" d="M ${LEFT_X} ${y} L ${blockLeft} ${y}" stroke="${escapeXml(row.leftColor)}" />`
      : '';
  const outgoing =
    row.rightFiber != null
      ? `<path class="fiber-line" d="M ${blockRight} ${y} L ${RIGHT_X} ${y}" stroke="${escapeXml(row.rightColor)}" />`
      : '';
  return `
    ${incoming}
    <rect class="fiber-block" x="${blockLeft}" y="${blockTop}" width="${BLOCK_W}" height="${BLOCK_H}" rx="4" />
    <text class="fiber-caption" x="${captionX}" y="${captionY}" text-anchor="middle">${escapeXml(row.caption)}</text>
    ${outgoing}
  `;
}

function directRowSvg(row: DiagramRow, y: number): string {
  return `
    <path class="fiber-line fiber-line--direct" d="M ${LEFT_X} ${y} L ${MID_X} ${y}" stroke="${escapeXml(row.leftColor)}" />
    <path class="fiber-line fiber-line--direct" d="M ${MID_X} ${y} L ${RIGHT_X} ${y}" stroke="${escapeXml(row.rightColor)}" />
  `;
}

function rowSvg(row: DiagramRow, index: number): string {
  const y = TOP + index * ROW_H;
  const leftLabel = row.leftFiber != null ? `vl. ${row.leftFiber}` : '';
  const rightLabel = row.rightFiber != null ? `vl. ${row.rightFiber}` : '';
  const body = row.mode === 'direct' ? directRowSvg(row, y) : blockRowSvg(row, y);
  return `
    <text class="fiber-end" x="${LEFT_X - 6}" y="${y + 4}" text-anchor="end">${escapeXml(leftLabel)}</text>
    <text class="fiber-end" x="${RIGHT_X + 6}" y="${y + 4}" text-anchor="start">${escapeXml(rightLabel)}</text>
    ${body}
  `;
}

export function renderRozvadecOccupancyFiberDiagram(schema: RozvadecOccupancySchema): string {
  const rows = diagramRows(schema);
  if (rows.length === 0) return '';
  const height = TOP + rows.length * ROW_H + 12;
  return `<section class="fiber-schema">
  <h2>Schéma vláken</h2>
  <svg viewBox="0 0 440 ${height}" width="440" height="${height}" role="img" aria-label="Schéma vláken: kazeta jako blok, spoje napřímo se potkávají uprostřed pod popiskem">
    ${rows.map(rowSvg).join('')}
  </svg>
</section>`;
}
